import React, {useState, useRef, useEffect, useCallback, useMemo} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {useLanguage} from '../../../_gori/i18n/Metronici18n'
import LanguageToggle from '../../modules/common/components/LanguageToggle'
import DemoPanel from '../../modules/demo/DemoPanel'

interface Video {
  title: string
  url: string
  duration: string
}

interface LocationState {
  videos: Video[]
  totalDuration: string
  allSubtopics?: string[]
  selectedSubtopics?: string[]
  startVideoIndex?: number
}

const DemoPlayerPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const intl = useIntl()
  const {language} = useLanguage()
  const videoRef = useRef<HTMLVideoElement>(null)

  const state = location.state as LocationState
  const originalVideos = useMemo(() => state?.videos || [], [state?.videos])
  const totalDuration = state?.totalDuration || '0 min'
  const allSubtopics = useMemo(() => state?.allSubtopics || [], [state?.allSubtopics])
  const selectedSubtopics = state?.selectedSubtopics || []
  const startVideoIndex = state?.startVideoIndex || 0

  // Calculate unselected subtopics for modal
  const unselectedSubtopics = allSubtopics.filter(
    (subtopic) => !selectedSubtopics.includes(subtopic)
  )
  const allWatched = unselectedSubtopics.length === 0
  const modalSubtopics = allWatched ? allSubtopics : unselectedSubtopics

  // Get translated subtopic names for current language (reactive to language changes)
  const translatedModalSubtopics = useMemo(() => {
    const subtopicKeys = [
      'DEMO_TOPICS_SUBTOPICS_CREATE_BATCH',
      'DEMO_TOPICS_SUBTOPICS_UPDATE_BATCH',
      'DEMO_TOPICS_SUBTOPICS_CREATE_LABELS',
      'DEMO_TOPICS_SUBTOPICS_NOTIFY_PARTNER',
    ]

    return modalSubtopics.map((subtopic, index) => {
      // Try to find the index of this subtopic in the original allSubtopics array
      const subtopicIndex = allSubtopics.indexOf(subtopic)
      const translationKey = subtopicKeys[subtopicIndex] || subtopicKeys[index]
      return translationKey ? intl.formatMessage({id: translationKey}) : subtopic
    })
  }, [modalSubtopics, allSubtopics, intl])

  // Transform video URLs and titles based on current language
  const videos = useMemo(() => {
    const subtopicKeys = [
      'DEMO_TOPICS_SUBTOPICS_CREATE_BATCH',
      'DEMO_TOPICS_SUBTOPICS_UPDATE_BATCH',
      'DEMO_TOPICS_SUBTOPICS_CREATE_LABELS',
      'DEMO_TOPICS_SUBTOPICS_NOTIFY_PARTNER',
    ]

    return originalVideos.map((video, index) => {
      // Use index to get the correct translation key
      const translationKey = subtopicKeys[index]
      const translatedTitle = translationKey
        ? intl.formatMessage({id: translationKey})
        : video.title

      return {
        ...video,
        title: translatedTitle,
        url: video.url.replace(/\/(en|ja|zh)\//, `/${language}/`),
      }
    })
  }, [originalVideos, language, intl])

  const [currentVideoIndex, setCurrentVideoIndex] = useState(startVideoIndex)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isPanelMinimized, setIsPanelMinimized] = useState(false)
  const [videoAspectRatio, setVideoAspectRatio] = useState('906/532')
  const [showFinishModal, setShowFinishModal] = useState(false)
  const [selectedVideos, setSelectedVideos] = useState<number[]>([])

  const currentVideo = videos[currentVideoIndex]

  const handleVideoEnd = useCallback(() => {
    if (currentVideoIndex < videos.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1)
    } else {
      setIsPlaying(false)
      setShowFinishModal(true)
    }
  }, [currentVideoIndex, videos.length])

  const handleVideoSelect = (index: number) => {
    setCurrentVideoIndex(index)
    setIsPlaying(true)
  }

  const handlePlayFirstVideo = () => {
    if (videos.length > 0) {
      setCurrentVideoIndex(0)
      if (videoRef.current) {
        videoRef.current.src = videos[0].url
        videoRef.current.load()
        videoRef.current.play()
      }
    }
  }

  useEffect(() => {
    if (videoRef.current && currentVideo) {
      videoRef.current.src = currentVideo.url
      videoRef.current.load()
    }
  }, [currentVideoIndex, currentVideo])

  // Reload video when language changes
  useEffect(() => {
    if (videoRef.current && currentVideo) {
      videoRef.current.src = currentVideo.url
      videoRef.current.load()
    }
  }, [language, currentVideo])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleMetadata = () => {
      const aspectRatio = `${video.videoWidth}/${video.videoHeight}`
      setVideoAspectRatio(aspectRatio)
    }

    video.addEventListener('ended', handleVideoEnd)
    video.addEventListener('loadedmetadata', handleMetadata)

    return () => {
      video.removeEventListener('ended', handleVideoEnd)
      video.removeEventListener('loadedmetadata', handleMetadata)
    }
  }, [handleVideoEnd])

  if (!videos.length) {
    return <div>{intl.formatMessage({id: 'DEMO_PLAYER_NO_VIDEOS'})}</div>
  }

  return (
    <div className='demo-container'>
      {/* Main Video Area */}
      <div className='demo-video-area'>
        {/* Video Container */}
        <div className='demo-video-container'>
          <video
            ref={videoRef}
            className='demo-video'
            style={{aspectRatio: videoAspectRatio}}
            controls={true}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
        </div>

        {/* Back Button */}
        <div className='demo-back-button'>
          <button
            onClick={() => navigate('/demo/topics')}
            className='btn d-flex align-items-center gap-1 demo-back-button-link'
          >
            {intl.formatMessage({id: 'DEMO_BACK_BUTTON'})}
          </button>
        </div>
      </div>

      {/* Right Panel */}
      <DemoPanel
        key={`player-${language}`}
        mode='player'
        totalDuration={totalDuration}
        videosCount={videos.length}
        videos={videos}
        currentVideoIndex={currentVideoIndex}
        isPlaying={isPlaying}
        isPanelMinimized={isPanelMinimized}
        onVideoSelect={handleVideoSelect}
        onPlayFirstVideo={handlePlayFirstVideo}
        onCollapsePanel={() => setIsPanelMinimized(!isPanelMinimized)}
      />

      <LanguageToggle />

      {/* Finish Modal */}
      {showFinishModal && (
        <div
          key={language}
          className='modal d-block demo-finish-modal'
          style={{backgroundColor: 'rgba(0,0,0,0.5)'}}
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h4 className='modal-title'>{intl.formatMessage({id: 'DEMO_MODAL_TITLE'})}</h4>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => setShowFinishModal(false)}
                ></button>
              </div>
              <div className='modal-body'>
                <p className='modal-subtitle mb-3'>
                  {intl.formatMessage({
                    id: allWatched ? 'DEMO_MODAL_SUBTITLE_REVISIT' : 'DEMO_MODAL_SUBTITLE_SKIPPED',
                  })}
                </p>

                {/* List of subtopics */}
                <div className='mb-4'>
                  {translatedModalSubtopics.map((subtopic, index) => (
                    <div
                      key={index}
                      className='demo-subtopic-container mb-2'
                      style={{
                        width: '100%',
                        borderRadius: '8px',
                        border: selectedVideos.includes(index)
                          ? '1px solid #14b8a6'
                          : '1px solid rgb(204, 204, 204)',
                      }}
                      onClick={() => {
                        if (selectedVideos.includes(index)) {
                          setSelectedVideos(selectedVideos.filter((i) => i !== index))
                        } else {
                          setSelectedVideos([...selectedVideos, index])
                        }
                      }}
                    >
                      <div
                        className={`demo-checkbox-small ${
                          selectedVideos.includes(index) ? 'checked' : ''
                        }`}
                        style={{
                          border: selectedVideos.includes(index)
                            ? '1px solid #14b8a6'
                            : '1px solid rgb(204, 204, 204)',
                          backgroundColor: selectedVideos.includes(index)
                            ? '#14b8a6'
                            : 'rgb(204, 204, 204)',
                        }}
                      />
                      <span className='demo-subtopic-text'>{subtopic}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => navigate('/demo')}
                >
                  {intl.formatMessage({id: 'DEMO_MODAL_NO_THANKS'})}
                </button>
                <button
                  type='button'
                  className='btn btn-advanced-primary'
                  disabled={selectedVideos.length === 0}
                  onClick={() => {
                    // Get selected subtopics from modal
                    const newlySelectedSubtopics = selectedVideos.map(
                      (index) => modalSubtopics[index]
                    )

                    // Navigate back to topics page with updated selections
                    navigate('/demo/topics', {
                      state: {
                        preSelectedSubtopics: allWatched
                          ? newlySelectedSubtopics
                          : [...selectedSubtopics, ...newlySelectedSubtopics],
                        autoStart: true,
                        newlyAddedSubtopics: newlySelectedSubtopics,
                      },
                    })
                  }}
                >
                  {intl.formatMessage({id: 'DEMO_MODAL_ADD_TO_DEMO'})}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DemoPlayerPage
